import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import styled from 'styled-components';
import {
  Checkbox,
  Counter,
  Icon,
  IconName,
  Spinner,
} from '@usealloy/component-library';

import { truncate } from '~/helpers/formatting';
import { formatCurrencyIntl } from '~/helpers/i18n';
import { StyledStatus } from '../status/TransactionStatusModal';

const OpenRow = styled.tr`
  &&&&& {
    border-left: none;
    border-right: none;
  }
`;

const ActionableRow = styled(OpenRow)`
  td:last-child {
    position: sticky;
    right: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-left: auto;
  }
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const StyledSanctionsIcon = styled(Icon)`
  margin-right: 0.5rem;
`;

const textLimit = 50;

// DATA ROW --------------------------------------------------
const DataRowComponent = ({
  trx,
  tableColumns,
  isActionable,
  actionComponent,
  isSelectable,
  selectedRows,
  setSelectedRows,
  onEvidenceClick,
}) => {
  // TODO: the "expandable" state is on the row itself, not the column within the row
  const [expanded, setExpanded] = useState({});
  const handleExpandClick = event => event.stopPropagation();
  const handleExpand = val => () => {
    const nextValue = val;
    setExpanded({ ...expanded, [nextValue]: !expanded[nextValue] });
  };

  const handleExpandClassName = (header, transaction) => {
    const shouldApplyExpandableClass = transaction[header]
      && typeof transaction[header] === 'string'
      && transaction[header].length >= textLimit;
    if (shouldApplyExpandableClass) {
      return `fas fa-fw fa-angle-${expanded[`toggler${transaction.transaction_id}`] ? 'left' : 'right'} clickable-element`;
    }
    return '';
  };

  const formatColumnData = (header, column, transaction) => {
    if ((column === 'null' || column === 'undefined') && header !== 'outcome') {
      return <span className="largest-text pad-0-8 grey">&#8212;</span>;
    }
    if (header === 'date' || header === 'settled_at') {
      return moment.utc(column).format('L');
    }
    if (header === 'amount') {
      const { formattedNumber } = formatCurrencyIntl(transaction.amount, {
        alphabeticCurrencyCode: transaction.currency_code,
      });
      return formattedNumber;
    }
    if (header === 'outcome') {
      const {
        outcome_fa: {
          IconComponent: OutcomeIcon,
        },
      } = transaction;
      return (
        OutcomeIcon
          ? <OutcomeIcon />
          : (
            <i className={`clickable-element ${transaction.outcome_fa.className}`} />
          )
      );
    }
    if (header === 'status') {
      return (
        <StyledStatus status={transaction[header]}>
          {transaction[header]}
        </StyledStatus>
      );
    }
    if (header === 'Counter-Party Sanctions') {
      const matches = transaction[header];

      if (matches > 0) {
        return (
          <>
            <StyledSanctionsIcon
              icon={IconName.Exclamation}
              title="Potential Matches"
              color="var(--cl-red-400)"
              size="large"
            />
            <span>{`${matches} Potential Match${matches > 1 ? 'es' : ''}`}</span>
          </>
        );
      }

      return <span className="largest-text pad-0-8 grey">&#8212;</span>;
    }
    if (header === 'evidence_count') {
      const evidenceCount = transaction[header];

      if (!evidenceCount || evidenceCount === 0) return '';

      return (
        <button
          type="button"
          onClick={() => onEvidenceClick?.(transaction)}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
          }}
          aria-label={`View ${evidenceCount} evidence item${evidenceCount > 1 ? 's' : ''}`}
        >
          <Counter count={evidenceCount} />
        </button>
      );
    }
    // by default, render string and make it expandable
    return (
      <>
        {expanded[`toggler${transaction.transaction_id}`] ? column : truncate(column, textLimit)}
        <span
          onClick={handleExpandClick}
          aria-hidden
        >
          <i
            id={`transaction-${transaction.transaction_id}`}
            className={handleExpandClassName(header, transaction)}
            onClick={handleExpand(`toggler${transaction.transaction_id}`)}
            aria-hidden
          />
        </span>
      </>
    );
  };

  // NOTE: duplicate code being returned due to CSS issues with the table
  if (isActionable) {
    return (
      <ActionableRow
        data-name="row"
        className="row-space-left row-space-right-medium"
      >
        {tableColumns.map(header => (
          <td
            className="no-wrap"
            key={header}
            data-name={header}
            aria-hidden
          >
            {formatColumnData(header, `${trx[header]}`, trx)}
          </td>
        ))}
        { actionComponent }
      </ActionableRow>
    );
  }
  const isSelected = selectedRows && selectedRows.filter(
    row => row.transaction_id === trx.transaction_id,
  ).length > 0;
  return (
    <OpenRow
      data-name="row"
      className="row-space-left row-space-right-medium"
    >
      {isSelectable && (
        <td>
          <CheckboxWrapper>
            <Checkbox
              data-testid="selectable_row_checkbox"
              className="archive-notification"
              checked={isSelected}
              onChange={() => setSelectedRows(prevSelectedRows => {
                if (isSelected) {
                  return selectedRows.filter(row => row.transaction_id !== trx.transaction_id);
                }
                return prevSelectedRows.concat([trx]);
              })}
            />
          </CheckboxWrapper>
        </td>
      )}
      {tableColumns.map(header => (
        <td
          className="no-wrap"
          key={header}
          data-name={header}
          aria-hidden
        >
          {formatColumnData(header, `${trx[header]}`, trx)}
        </td>
      ))}
    </OpenRow>
  );
};

DataRowComponent.propTypes = {
  trx: PropTypes.object.isRequired,
  tableColumns: PropTypes.arrayOf(PropTypes.string),
  isActionable: PropTypes.bool.isRequired,
  actionComponent: PropTypes.object,
  isSelectable: PropTypes.bool.isRequired,
  selectedRows: PropTypes.arrayOf(PropTypes.object),
  setSelectedRows: PropTypes.func.isRequired,
  onEvidenceClick: PropTypes.func,
};

export const DataRow = DataRowComponent;

// LOADING ROW --------------------------------------------------
const LoadingRowPropTypes = {
  colSpan: PropTypes.number.isRequired,
};
const LoadingRowComponent = ({ colSpan }) => (
  <OpenRow className="row-space-left row-space-right-medium">
    <td className="text-center" colSpan={colSpan}>
      <Spinner />
      {' loading...'}
    </td>
  </OpenRow>
);
LoadingRowComponent.propTypes = LoadingRowPropTypes;
export const LoadingRow = LoadingRowComponent;

// EMPTY ROW --------------------------------------------------
const EmptyRowPropTypes = {
  colSpan: PropTypes.number.isRequired,
};
const EmptyRowComponent = ({ colSpan }) => (
  <OpenRow className="row-space-left row-space-right-medium">
    <td className="grey" colSpan={colSpan}>
      No Transaction Activity
    </td>
  </OpenRow>
);
EmptyRowComponent.propTypes = EmptyRowPropTypes;
export const EmptyRow = EmptyRowComponent;
